import { useQuery, useMutation } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export interface VoiceCatalogueDto {
  id: string;
  name: string;
  gender: string;
  language: string;
  accent: string;
  style: string;
  provider: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface VoiceSearchFilters {
  gender?: string;
  language?: string;
  style?: string;
  search?: string;
}

export function useSearchVoices(filters: VoiceSearchFilters) {
  const { getToken } = useAuth();
  return useQuery<VoiceCatalogueDto[]>({
    queryKey: ['voices', filters],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const params = new URLSearchParams();
      if (filters.gender) params.set('gender', filters.gender);
      if (filters.language) params.set('language', filters.language);
      if (filters.style) params.set('style', filters.style);
      if (filters.search) params.set('search', filters.search);
      const response = await client
        .get(`api/v2/voices/search?${params.toString()}`)
        .json<ApiResponse<VoiceCatalogueDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useUpdateBookSettings(bookId: string) {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      artStyle?: string | null;
      narratorVoiceId?: string | null;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .put(`api/v2/books/${bookId}/settings`, { json: input })
        .json<ApiResponse<unknown>>();
      return response.data;
    },
  });
}

export function useSetChapterAmbientAudio(chapterId: string | undefined) {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (ambientAudioUrl: string | null) => {
      if (!chapterId) return;
      const client = createApiClient(getToken);
      await client.put(`api/v2/chapters/${chapterId}/ambient-audio`, {
        json: { ambientAudioUrl },
      });
    },
  });
}
